// ============================================================
// home-search/premium-card.js — Tarjeta premium en ventana nueva
// ============================================================

import { withBackendOrigin, safeUrl } from './utils.js';
import { getImageUrl } from '../api-config.js';
import { isPremium } from './premium-detector.js';

/**
 * Crea un elemento DOM de badge verificado (SVG inline)
 */
export function createVerifiedBadgeElement() {
  const span = document.createElement('span');
  span.className = 'verified-badge';
  span.title = 'Cuenta verificada';
  span.setAttribute('aria-label', 'Cuenta verificada');
  span.setAttribute('role', 'img');

  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.classList.add('verified-badge-icon');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('width', '28');
  svg.setAttribute('height', '28');

  const path = document.createElementNS(svgNS, 'path');
  path.setAttribute('d', 'M12 2l2.39 2.39 3.38-.49.49 3.38L20.99 10 18.6 12.39l.49 3.38-3.38.49L12 19.99l-2.39-2.39-3.38.49-.49-3.38L3.01 10l2.39-2.39-.49-3.38 3.38.49L12 2zm-1.2 12.2l5-5-1.4-1.4-3.6 3.6-1.6-1.6-1.4 1.4 3 3z');
  svg.appendChild(path);
  span.appendChild(svg);
  return span;
}

/**
 * HTML string de badge verificado (para ventana nueva)
 */
export function verifiedBadgeHTML() {
  return `
    <span class="verified-badge" title="Cuenta verificada" aria-label="Cuenta verificada" role="img">
      <svg class="verified-badge-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width="28" height="28">
        <path d="M12 2l2.39 2.39 3.38-.49.49 3.38L20.99 10 18.6 12.39l.49 3.38-3.38.49L12 19.99l-2.39-2.39-3.38.49-.49-3.38L3.01 10l2.39-2.39-.49-3.38 3.38.49L12 2zm-1.2 12.2l5-5-1.4-1.4-3.6 3.6-1.6-1.6-1.4 1.4 3 3z"/>
      </svg>
    </span>
  `;
}

/**
 * Abre una nueva ventana con SOLO la tarjeta del negocio premium
 */
export function openPremiumWindowForBusiness(negocio) {
  try {
    const win = window.open('about:blank', '_blank');
    if (!win) {
      alert('Por favor permite ventanas emergentes para ver la tarjeta premium.');
      return;
    }

    let bannerUrl = 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
    if (negocio.banner_url) {
      const raw = String(negocio.banner_url).trim();
      const isAbsolute = /^https?:\/\//i.test(raw);
      if (isAbsolute) {
        bannerUrl = raw;
      } else if (raw.startsWith('/uploads/')) {
        bannerUrl = withBackendOrigin(raw);
      } else if (!raw.includes('/')) {
        bannerUrl = withBackendOrigin(`/uploads/banners/${raw}`);
      } else {
        bannerUrl = withBackendOrigin(getImageUrl(raw));
      }
    }
    const isAbsoluteBanner = /^https?:\/\//i.test(bannerUrl);
    const finalBannerUrl = safeUrl(isAbsoluteBanner ? bannerUrl : `${bannerUrl}${bannerUrl.includes('?') ? '&' : '?'}t=${Date.now()}`) || 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';

    let logoUrl = 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
    if (negocio.logo_url) {
      const raw = String(negocio.logo_url).trim();
      const isAbsolute = /^https?:\/\//i.test(raw);
      if (isAbsolute) {
        logoUrl = raw;
      } else if (raw.startsWith('/uploads/')) {
        logoUrl = withBackendOrigin(raw);
      } else if (!raw.includes('/')) {
        logoUrl = withBackendOrigin(`/uploads/logos/${raw}`);
      } else {
        logoUrl = withBackendOrigin(getImageUrl(raw));
      }
      const isAbsoluteLogo = /^https?:\/\//i.test(logoUrl);
      if (!isAbsoluteLogo) {
        logoUrl = `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
      }
    }
    logoUrl = safeUrl(logoUrl) || 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';

    const nameText = String(negocio.name || '');
    const descText = String(negocio.description || 'Descripción no disponible');
    const cityText = String(negocio.city || 'Ciudad');
    const phoneRaw = String(negocio.phone || '');
    const phoneDigits = phoneRaw.replace(/[^\d+]/g, '');
    const vitrinaHref = (isPremium(negocio) && negocio.slug) ? `/vitrina-plus.html?slug=${encodeURIComponent(negocio.slug)}` : '';

    const d = win.document;
    try { d.documentElement.lang = 'es'; } catch {}

    d.title = nameText ? `${nameText} - Tarjeta` : 'Tarjeta';
    while (d.head && d.head.firstChild) d.head.removeChild(d.head.firstChild);
    while (d.body && d.body.firstChild) d.body.removeChild(d.body.firstChild);

    const addMeta = (name, content) => {
      const m = d.createElement('meta');
      m.setAttribute(name, content);
      d.head.appendChild(m);
    };
    addMeta('charset', 'utf-8');
    const mv = d.createElement('meta');
    mv.setAttribute('name', 'viewport');
    mv.setAttribute('content', 'width=device-width, initial-scale=1');
    d.head.appendChild(mv);

    const icon = d.createElement('link');
    icon.setAttribute('rel', 'icon');
    icon.setAttribute('href', '/favicon.ico');
    d.head.appendChild(icon);

    const style = d.createElement('style');
    style.textContent = `body{background:#f3f4f6;margin:0;font-family:system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,'Helvetica Neue',Arial,'Noto Sans','Apple Color Emoji','Segoe UI Emoji';} .wrap{max-width:840px;margin:40px auto;padding:24px} .card{background:#fff;border-radius:16px;box-shadow:0 8px 32px rgba(74,144,226,0.08);overflow:hidden} .banner{position:relative;height:220px;overflow:hidden} .banner img{width:100%;height:100%;object-fit:cover} .logo{position:absolute;left:16px;bottom:90px;width:64px;height:64px;border-radius:50%;overflow:hidden;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.15);background:#f9fafb;display:flex;align-items:center;justify-content:center} .logo img{width:100%;height:100%;object-fit:cover} .content{padding:20px} h1{margin:10px 0 10px 0;font-size:1.25rem;color:#111827} p{margin:0 0 12px 0;color:#4b5563} .meta{display:flex;gap:16px;color:#6b7280;font-size:0.95rem;flex-wrap:wrap} .btns{margin-top:12px}`;
    d.head.appendChild(style);

    const wrap = d.createElement('div');
    wrap.className = 'wrap';

    const card = d.createElement('div');
    card.className = 'card';

    const banner = d.createElement('div');
    banner.className = 'banner';

    const bannerImg = d.createElement('img');
    bannerImg.src = finalBannerUrl;
    bannerImg.alt = nameText;

    const logoWrap = d.createElement('div');
    logoWrap.className = 'logo';
    const logoImg = d.createElement('img');
    logoImg.src = logoUrl;
    logoImg.alt = nameText ? `Logo ${nameText}` : 'Logo';
    logoWrap.appendChild(logoImg);

    banner.appendChild(bannerImg);
    banner.appendChild(logoWrap);

    const content = d.createElement('div');
    content.className = 'content';
    const h1 = d.createElement('h1');
    h1.textContent = nameText;
    const p = d.createElement('p');
    p.textContent = descText;
    const meta = d.createElement('div');
    meta.className = 'meta';

    const citySpan = d.createElement('span');
    citySpan.textContent = cityText;

    const phoneSpan = d.createElement('span');
    if (phoneDigits) {
      const a = d.createElement('a');
      a.href = `tel:${phoneDigits}`;
      a.style.cssText = 'color:#10b981;text-decoration:none;';
      a.textContent = phoneRaw;
      phoneSpan.appendChild(a);
    } else {
      phoneSpan.textContent = 'Teléfono no disponible';
    }

    meta.appendChild(citySpan);
    meta.appendChild(phoneSpan);

    const btns = d.createElement('div');
    btns.className = 'btns';
    if (vitrinaHref) {
      const a = d.createElement('a');
      a.href = vitrinaHref;
      a.textContent = 'Vitrina+';
      a.style.cssText = 'display:inline-flex;align-items:center;gap:0.5rem;background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);color:white;text-decoration:none;padding:0.5rem 1rem;border-radius:0.5rem;font-weight:600;font-size:0.85rem;box-shadow:0 2px 8px rgba(102,126,234,0.3);';
      btns.appendChild(a);
    }

    content.appendChild(h1);
    content.appendChild(p);
    content.appendChild(meta);
    content.appendChild(btns);

    card.appendChild(banner);
    card.appendChild(content);
    wrap.appendChild(card);
    d.body.appendChild(wrap);
  } catch (_) {
    // noop
  }
}
